import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  BookOpen, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  EyeOff,
  HelpCircle,
  GripVertical
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable exam item component
const SortableExamItem = ({ exam, onToggle, onDelete, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exam.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bento-item flex items-center justify-between ${isDragging ? "shadow-lg border-primary" : ""}`}
      data-testid={`exam-item-${exam.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 -ml-2 text-muted-foreground hover:text-foreground transition-colors touch-none"
          title="Arraste para reordenar"
          data-testid={`drag-handle-${exam.id}`}
        >
          <GripVertical size={20} />
        </button>
        
        <div className="w-12 h-12 border flex items-center justify-center">
          <BookOpen size={20} strokeWidth={1} />
        </div>
        <div>
          <h3 className="font-serif text-lg">{exam.title}</h3>
          <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
            <span className="flex items-center gap-1">
              <HelpCircle size={12} />
              {exam.question_count} questões
            </span>
            {!exam.is_active && (
              <span className="text-yellow-600">Desativado</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggle(exam.id)}
          title={exam.is_active ? "Desativar" : "Ativar"}
          data-testid={`toggle-${exam.id}`}
        >
          {exam.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
        </Button>
        <Link to={`/admin/exams/${exam.id}`}>
          <Button variant="outline" size="sm" title="Editar" data-testid={`edit-${exam.id}`}>
            <Edit size={14} />
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(exam)}
          title="Excluir"
          data-testid={`delete-${exam.id}`}
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};

export const AdminExams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteExam, setDeleteExam] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/admin/exams`);
      setExams(response.data);
    } catch (error) {
      toast.error("Erro ao carregar simulados");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = exams.findIndex((e) => e.id === active.id);
      const newIndex = exams.findIndex((e) => e.id === over.id);

      const newExams = arrayMove(exams, oldIndex, newIndex);
      setExams(newExams);

      // Save new order to backend
      try {
        await axios.post(`${API}/admin/exams/reorder`, {
          exam_ids: newExams.map((e) => e.id),
        });
        toast.success("Ordem atualizada!");
      } catch (error) {
        toast.error("Erro ao salvar ordem");
        fetchExams(); // Revert on error
      }
    }
  };

  const handleToggle = async (examId) => {
    try {
      const response = await axios.patch(`${API}/admin/exams/${examId}/toggle`);
      toast.success(response.data.is_active ? "Simulado ativado!" : "Simulado desativado!");
      fetchExams();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  const handleDelete = async () => {
    if (!deleteExam) return;
    
    try {
      await axios.delete(`${API}/admin/exams/${deleteExam.id}`);
      toast.success("Simulado excluído!");
      setDeleteExam(null);
      fetchExams();
    } catch (error) {
      toast.error("Erro ao excluir simulado");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="admin-exams-page">
        {/* Header */}
        <div className="admin-header">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Administração
            </p>
            <h1 className="admin-title">Simulados</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Arraste os itens para reordenar
            </p>
          </div>
          <Link to="/admin/exams/new">
            <Button className="btn-primary" data-testid="new-exam-button">
              <Plus size={16} className="mr-2" />
              Novo Simulado
            </Button>
          </Link>
        </div>

        {/* Exams List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 loading-skeleton" />
            ))}
          </div>
        ) : exams.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={exams.map((e) => e.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {exams.map((exam) => (
                  <SortableExamItem
                    key={exam.id}
                    exam={exam}
                    onToggle={handleToggle}
                    onDelete={setDeleteExam}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="empty-state">
            <BookOpen className="empty-state-icon" />
            <p className="empty-state-title">Nenhum simulado criado</p>
            <p className="empty-state-description">
              Crie seu primeiro simulado para que os alunos possam praticar.
            </p>
            <Link to="/admin/exams/new" className="mt-6 inline-block">
              <Button className="btn-primary">
                <Plus size={16} className="mr-2" />
                Criar Simulado
              </Button>
            </Link>
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteExam} onOpenChange={() => setDeleteExam(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl">
                Excluir simulado?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. O simulado "{deleteExam?.title}" será permanentemente excluído.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminExams;
